"use client";

import React, { useEffect, useState } from "react";
import dynamic from "next/dynamic";
import { ApexOptions } from "apexcharts";

// Dynamically import ReactApexChart to avoid SSR issues
const ReactApexChart = dynamic(() => import("react-apexcharts"), {
	ssr: false,
	loading: () => (
		<div className="w-full h-64 bg-gray-100 animate-pulse rounded"></div>
	),
});

interface ApexChartProps {
	data: number[];
	categories: string[];
	type?:
		| "line"
		| "area"
		| "bar"
		| "pie"
		| "donut"
		| "radialBar"
		| "scatter"
		| "bubble"
		| "heatmap"
		| "candlestick"
		| "boxPlot"
		| "radar"
		| "polarArea"
		| "rangeBar"
		| "rangeArea"
		| "treemap";
	height?: number | string;
	width?: number | string;
	title?: string;
}

const ApexChart: React.FC<ApexChartProps> = ({
	data,
	categories,
	type = "bar",
	height = 350,
	width = "100%",
	title = "Chart",
}) => {
	const [isClient, setIsClient] = useState(false);

	useEffect(() => {
		setIsClient(true);
	}, []);

	// Create chart options inside the client component
	const getChartOptions = (): ApexOptions => {
		const baseOptions: ApexOptions = {
			chart: {
				type: type,
				toolbar: {
					show: false,
				},
			},
			tooltip: {
				y: {
					formatter: function (val: any) {
						return val + "%";
					},
				},
			},
		};

		if (type === "pie" || type === "donut") {
			return {
				...baseOptions,
				colors: ["#3b82f6", "#10b981", "#f59e0b", "#ef4444"],
				labels: categories,
				dataLabels: {
					enabled: true,
					formatter: function (val: any) {
						return Math.round(val) + "%";
					},
					style: {
						fontSize: "12px",
						colors: ["#FFFFFF"],
						fontWeight: "bold",
					},
					dropShadow: {
						enabled: false,
					},
				},
				plotOptions: {
					pie: {
						expandOnClick: false,
						dataLabels: {
							offset: -10,
						},
					},
				},
				legend: {
					position: "bottom",
					horizontalAlign: "center",
					fontSize: "14px",
					markers: {
						size: 8,
					},
				},
			};
		} else {
			return {
				...baseOptions,
				colors: ["#0050FF"],
				plotOptions: {
					bar: {
						horizontal: false,
						borderRadius: 4,
						columnWidth: "60%",
						dataLabels: {
							position: "top",
						},
					},
				},
				dataLabels: {
					enabled: false,
					formatter: function (val: any) {
						return val + "%";
					},
					offsetY: -20,
					style: {
						fontSize: "12px",
						colors: ["#304758"],
						fontWeight: "bold",
					},
				},
				xaxis: {
					categories: categories,
					labels: {
						formatter: function (val: any) {
							return val;
						},
						style: {
							fontSize: "14px",
							fontWeight: 500,
						},
					},
				},
				yaxis: {
					labels: {
						formatter: function (val: any) {
							return val + "%";
						},
						style: {
							fontSize: "12px",
							colors: ["#6B7280"],
						},
					},
					max: 100,
				},
				grid: {
					borderColor: "#f0f0f0",
					strokeDashArray: 3,
				},
			};
		}
	};

	const chartOptions = getChartOptions();

	// For pie/donut charts, series is just the data array
	// For other charts, series is an object with name and data
	const series =
		type === "pie" || type === "donut"
			? data
			: [
					{
						name: title,
						data: data,
					},
				];

	if (!isClient) {
		return (
			<div
				className="w-full bg-gray-100 animate-pulse rounded flex items-center justify-center"
				style={{ height: typeof height === "number" ? `${height}px` : height }}
			>
				<span className="text-gray-500 text-sm">Loading chart...</span>
			</div>
		);
	}

	return (
		<div className="w-full">
			<ReactApexChart
				options={chartOptions}
				series={series}
				type={type}
				height={height}
				width={width}
			/>
		</div>
	);
};

export default ApexChart;
